import { Link, useSearchParams } from 'react-router-dom'
import { Button, Card, EmptyState, InlineError, Media, Skeleton } from '../../components/ui'
import { BranchCard, BusinessHours, StaffCard } from '../../features/catalog'
import { useGetBranchesQuery, useGetStaffQuery } from '../../services/catalogApi'
import { useTenant } from '../../tenant/useTenant'
import type { Branch } from '../../types/storefront'

/** Branch list, plus a detail view when ?branch=<id> pins one of them. */
export function BranchesPage() {
  const { slug } = useTenant()
  const [params] = useSearchParams()
  const branches = useGetBranchesQuery(slug)

  const selectedId = Number(params.get('branch'))
  const selected = branches.data?.find((b) => b.id === selectedId) ?? null

  if (branches.error) {
    return (
      <div className="container-page py-section-gap-md md:py-section-gap-lg">
        <InlineError error={branches.error} onRetry={branches.refetch} />
      </div>
    )
  }

  if (branches.isLoading) {
    return (
      <div className="container-page py-section-gap-md md:py-section-gap-lg">
        <Skeleton shape="rect" className="mb-12 h-12 w-64" />
        <div className="grid grid-cols-1 gap-8 md:grid-cols-2 lg:grid-cols-3">
          {Array.from({ length: 3 }, (_, i) => (
            <Skeleton key={i} shape="rect" className="h-80 w-full" />
          ))}
        </div>
      </div>
    )
  }

  if (!branches.data?.length) {
    return (
      <div className="container-page py-section-gap-md md:py-section-gap-lg">
        <EmptyState
          icon="storefront"
          title="No locations yet"
          body="The salon has not listed an address yet. Call us and we will help."
          action={<Button to={`/${slug}/book`}>Book now</Button>}
        />
      </div>
    )
  }

  if (selected) return <BranchDetail slug={slug} branch={selected} />

  return (
    <div className="container-page py-section-gap-md md:py-section-gap-lg">
      <header className="mb-16 max-w-2xl md:mb-24">
        <h1 className="mb-4 font-display text-headline-lg-mobile text-primary md:text-headline-lg">Locations</h1>
        <p className="font-body-lg text-body-lg text-on-surface-variant">
          Choose the location that suits you best, then book the time that fits your day.
        </p>
      </header>

      <div className="grid grid-cols-1 gap-8 md:grid-cols-2 lg:grid-cols-3">
        {branches.data.map((branch) => (
          <BranchCard
            key={branch.id}
            branch={branch}
            footer={
              <>
                <Button to={`/${slug}/branches?branch=${branch.id}`} variant="outlined" size="sm">
                  Details
                </Button>
                <Button to={`/${slug}/services?branch=${branch.id}`} variant="text" size="sm">
                  Services
                </Button>
              </>
            }
          />
        ))}
      </div>
    </div>
  )
}

function BranchDetail({ slug, branch }: { slug: string; branch: Branch }) {
  const staff = useGetStaffQuery({ slug, branchId: branch.id })
  const address = [branch.address, branch.city].filter(Boolean).join(', ')

  return (
    <div className="container-page py-section-gap-md md:py-section-gap-lg">
      <Link
        to={`/${slug}/branches`}
        className="mb-8 inline-flex items-center gap-2 font-label-caps text-label-caps uppercase text-primary hover:underline underline-offset-4"
      >
        <span className="material-symbols-outlined text-[16px]">arrow_back</span>
        All locations
      </Link>

      <div className="mb-16 aspect-[21/9] w-full overflow-hidden rounded-lg border border-primary/10 bg-surface-container-high">
        <Media src={branch.image} alt={branch.name} kind="branch" eager />
      </div>

      <div className="grid grid-cols-1 gap-12 lg:grid-cols-2">
        <div className="flex flex-col gap-3">
          <h1 className="font-display text-headline-lg-mobile text-primary md:text-headline-lg">{branch.name}</h1>
          {address && <p className="font-body-md text-body-md text-on-surface-variant">{address}</p>}
          {branch.phone && (
            <a href={`tel:${branch.phone}`} className="font-body-md text-body-md text-primary hover:underline">
              {branch.phone}
            </a>
          )}
          <div className="mt-6 flex flex-wrap gap-3">
            <Button to={`/${slug}/book`}>Book now</Button>
            <Button to={`/${slug}/services?branch=${branch.id}`} variant="outlined">
              View services
            </Button>
          </div>
        </div>

        <Card variant="tonal">
          <h2 className="mb-4 font-label-caps text-label-caps uppercase text-primary">Opening hours</h2>
          <BusinessHours hours={branch.business_hours} />
        </Card>
      </div>

      {staff.data && staff.data.length > 0 && (
        <section className="mt-section-gap-md">
          <h2 className="mb-8 font-display text-headline-md text-on-surface">Specialists at this location</h2>
          <ul className="flex gap-8 overflow-x-auto pb-2 no-scrollbar">
            {staff.data.map((member) => (
              <li key={member.id}>
                <StaffCard staff={member} />
              </li>
            ))}
          </ul>
        </section>
      )}
    </div>
  )
}
